import type { AlertEvery, AlertSchedule } from '../types/gotypes'

/**
 * When a table or question alert runs (alerts round, 12 Oct 2026): daily,
 * weekly on a weekday or monthly on a day, at one time of day in the rule's
 * IANA time zone. Structured fields, never cron, so this picker, the agent's
 * tools and the loop all read the same shape without a parser. Mirrors
 * goapi/pkg/types/alerts.go AlertSchedule and goapi/pkg/alerts/schedule.go.
 */

/** The server's default zone (types.DefaultAlertTimeZone). */
export const DEFAULT_ALERT_TIME_ZONE = 'Europe/London'

export const ALERT_EVERY_OPTIONS: { value: AlertEvery; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
]

/** 0 Sunday … 6 Saturday, as Go's time.Weekday. */
export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/** The viewer's own zone, so "09:00" means 09:00 where they are. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_ALERT_TIME_ZONE
  } catch {
    return DEFAULT_ALERT_TIME_ZONE
  }
}

/** A new alert's schedule: every day at 09:00 in the viewer's zone. */
export function defaultAlertSchedule(): AlertSchedule {
  return { every: 'daily', at: '09:00', timezone: browserTimeZone() }
}

/** "HH:MM", 24h, as the server's ValidateSchedule accepts it. */
export function isValidAt(at: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(at)
}

/** The schedule as sent: only the fields its shape uses. */
export function scheduleBody(s: AlertSchedule): AlertSchedule {
  const base = { every: s.every, at: s.at, timezone: s.timezone || DEFAULT_ALERT_TIME_ZONE }
  if (s.every === 'weekly') return { ...base, weekday: s.weekday ?? 1 }
  if (s.every === 'monthly') return { ...base, day: s.day ?? 1 }
  return base
}

/** A new shape keeping the time and zone; weekly starts on Monday, monthly on the 1st. */
export function scheduleForEvery(s: AlertSchedule, every: AlertEvery): AlertSchedule {
  return scheduleBody({ every, at: s.at, timezone: s.timezone, weekday: s.weekday, day: s.day })
}

export function sameSchedule(a: AlertSchedule | undefined, b: AlertSchedule | undefined): boolean {
  if (!a || !b) return a === b
  return JSON.stringify(scheduleBody(a)) === JSON.stringify(scheduleBody(b))
}

/** "Daily at 09:00 (Europe/London)", "Weekly on Monday at 09:00 (…)", "Monthly on day 31 at 09:00 (…)". */
export function describeSchedule(s: AlertSchedule | undefined): string {
  if (!s) return 'Not set'
  const zone = s.timezone || DEFAULT_ALERT_TIME_ZONE
  const when = s.every === 'weekly'
    ? `Weekly on ${WEEKDAYS[s.weekday ?? 1] ?? 'Monday'}`
    : s.every === 'monthly'
      ? `Monthly on day ${s.day ?? 1}`
      : 'Daily'
  return `${when} at ${s.at} (${zone})`
}
